import {
  Component,
  DestroyRef,
  ElementRef,
  PLATFORM_ID,
  computed,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { SliderModule } from 'primeng/slider';
import { RatingModule } from 'primeng/rating';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';

import { Player, RatingMode, TeamComposition, ratingToStars, starsToRating } from '../player.model';
import { TeamBalancerService } from '../team-balancer.service';
import { RosterStore, RemovedPlayer } from '../roster.store';
import { ThemeService } from '../theme.service';
import { SeoService } from '../seo.service';
import { PwaService } from '../pwa.service';
import { ImportDialogComponent, ImportedPlayer } from './import-dialog/import-dialog.component';
import { LegalDialogComponent } from './legal-dialog/legal-dialog.component';
import { ContactDialogComponent } from './contact-dialog/contact-dialog.component';

const SUPPORTED_LANGS = ['fr', 'en', 'es', 'it', 'pt', 'de', 'tr'];
const UNDO_DELAY = 7000;
/** Au-delà, un champ de recherche devient plus rapide que le défilement. */
const SEARCH_THRESHOLD = 8;

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    FormsModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    SliderModule,
    RatingModule,
    TranslateModule,
    SelectModule,
    TooltipModule,
    ImportDialogComponent,
    LegalDialogComponent,
    ContactDialogComponent
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent {
  private seo = inject(SeoService);
  private balancer = inject(TeamBalancerService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private messageService = inject(MessageService);
  private translate = inject(TranslateService);
  private platformId = inject(PLATFORM_ID);
  private destroyRef = inject(DestroyRef);

  readonly pwa = inject(PwaService);
  readonly theme = inject(ThemeService);
  readonly store = inject(RosterStore);

  private nameInput = viewChild<ElementRef<HTMLInputElement>>('nameInput');
  private editInput = viewChild<ElementRef<HTMLInputElement>>('editInput');

  // ------------------------------------------------------------------- langue
  readonly languages = [
    { label: 'Français', value: 'fr', flag: 'fr' },
    { label: 'English', value: 'en', flag: 'gb' },
    { label: 'Español', value: 'es', flag: 'es' },
    { label: 'Italiano', value: 'it', flag: 'it' },
    { label: 'Português', value: 'pt', flag: 'pt' },
    { label: 'Deutsch', value: 'de', flag: 'de' },
    { label: 'Türkçe', value: 'tr', flag: 'tr' }
  ];
  readonly currentLang = signal('fr');

  // ----------------------------------------------------------------- UI state
  readonly visibleLegal = signal(false);
  readonly visibleImport = signal(false);
  readonly visibleContact = signal(false);

  readonly newPlayerName = signal('');
  /** Toujours sur l'échelle canonique 0-100. */
  readonly newPlayerRating = signal(50);

  readonly search = signal('');
  readonly editingId = signal<string | null>(null);
  readonly editingName = signal('');
  readonly removed = signal<RemovedPlayer | null>(null);
  readonly liveMessage = signal('');

  private undoTimer: ReturnType<typeof setTimeout> | null = null;

  // ----------------------------------------------------------------- computed
  readonly ratingMode = computed(() => this.store.ratingMode());

  readonly visiblePlayers = computed(() => {
    const term = this.search().trim().toLowerCase();
    const players = this.store.players();
    if (!term) return players;
    return players.filter((p) => p.name.toLowerCase().includes(term));
  });

  readonly showSearch = computed(() => this.store.players().length >= SEARCH_THRESHOLD);

  readonly newPlayerStars = computed(() => ratingToStars(this.newPlayerRating()));

  readonly teamSizeHint = computed(() => {
    const count = this.store.playingPlayers().length;
    return { a: Math.ceil(count / 2), b: Math.floor(count / 2) };
  });

  /** `null` = auto: aucun plafond, l'effectif entier est réparti. */
  readonly teamSizeOptions: { value: number | null }[] = [
    { value: null },
    ...[5, 6, 7, 8, 9, 10, 11].map((value) => ({ value }))
  ];

  constructor() {
    this.translate.addLangs(SUPPORTED_LANGS);
    this.translate.setDefaultLang('fr');

    if (isPlatformBrowser(this.platformId)) {
      const stored = localStorage.getItem('equifoot.lang');
      const browserLang = this.translate.getBrowserLang();
      const lang =
        stored && SUPPORTED_LANGS.includes(stored)
          ? stored
          : browserLang && SUPPORTED_LANGS.includes(browserLang)
            ? browserLang
            : 'fr';
      this.translate.use(lang);
      this.currentLang.set(lang);
    } else {
      this.translate.use('fr');
    }

    // Lecture unique au chargement: un lien partagé arrive toujours par une
    // navigation initiale. S'abonner au flux ferait réimporter notre propre
    // paramètre à chaque génération, ce qui réécrirait l'effectif local.
    const shared = this.route.snapshot.queryParamMap.get('teams');
    if (shared) this.restoreShared(shared);

    this.destroyRef.onDestroy(() => {
      if (this.undoTimer) clearTimeout(this.undoTimer);
    });
  }

  // ------------------------------------------------------------------- langue
  changeLang(lang: string) {
    this.translate.use(lang);
    this.currentLang.set(lang);
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem('equifoot.lang', lang);
        document.documentElement.setAttribute('lang', lang);
      } catch {
        // stockage indisponible, sans conséquence
      }
    }
  }

  toggleTheme() {
    this.theme.toggle();
  }

  // -------------------------------------------------------------------- notes
  setRatingMode(mode: RatingMode) {
    this.store.setRatingMode(mode);
  }

  /** Valeur affichée pour un joueur, selon le mode courant. */
  displayRating(player: Player): number {
    return this.ratingMode() === 'STARS' ? ratingToStars(player.rating) : player.rating;
  }

  /**
   * Ramène un agrégat canonique (total, moyenne, écart) dans l'échelle
   * affichée: un total de 150 devient 7,5 en mode étoiles.
   */
  toScale(value: number): string {
    if (this.ratingMode() !== 'STARS') return String(Math.round(value));
    return String(Math.round((value / 20) * 10) / 10);
  }

  onNewStarsChange(stars: number) {
    this.newPlayerRating.set(starsToRating(stars));
  }

  onPlayerStarsChange(player: Player, stars: number) {
    this.store.setRating(player.id, starsToRating(stars));
  }

  onPlayerScoreChange(player: Player, input: HTMLInputElement) {
    const parsed = Number.parseInt(input.value, 10);

    // Champ vidé ou saisie invalide: on remet la valeur courante, sinon
    // l'affichage mentirait sur la note réellement enregistrée.
    if (Number.isNaN(parsed)) {
      input.value = String(player.rating);
      return;
    }

    const clamped = Math.min(100, Math.max(0, parsed));
    input.value = String(clamped);
    this.store.setRating(player.id, clamped);
  }

  // ------------------------------------------------------------------ effectif
  addPlayer() {
    const created = this.store.addPlayer(this.newPlayerName(), this.newPlayerRating());
    if (!created) return;

    this.newPlayerName.set('');

    // [ngModel] est unidirectionnel: quand la valeur enregistrée par Angular
    // est déjà '', il ne réécrit pas le champ. On le vide donc explicitement,
    // et on rend le focus pour enchaîner les ajouts au clavier.
    const input = this.nameInput()?.nativeElement;
    if (input) {
      input.value = '';
      input.focus();
    }

    this.announce('A11Y_PLAYER_ADDED', { name: created.name });
  }

  startEdit(player: Player) {
    this.editingId.set(player.id);
    this.editingName.set(player.name);

    // select() ne donne pas le focus: sans focus(), la frappe part ailleurs.
    setTimeout(() => {
      const input = this.editInput()?.nativeElement;
      input?.focus();
      input?.select();
    });
  }

  commitEdit() {
    const id = this.editingId();
    if (id) this.store.rename(id, this.editingName());
    this.cancelEdit();
  }

  cancelEdit() {
    this.editingId.set(null);
    this.editingName.set('');
  }

  toggleActive(player: Player) {
    this.store.toggleActive(player.id);
  }

  toggleBenched(player: Player) {
    this.store.toggleBenched(player.id);
  }

  onTeamSizeChange(size: number | null) {
    this.store.setTeamSize(size);
  }

  setAllActive(active: boolean) {
    this.store.setAllActive(active);
  }

  removePlayer(player: Player) {
    const removed = this.store.remove(player.id);
    if (!removed) return;

    this.removed.set(removed);
    this.announce('A11Y_PLAYER_REMOVED', { name: removed.player.name });

    if (this.undoTimer) clearTimeout(this.undoTimer);
    this.undoTimer = setTimeout(() => this.removed.set(null), UNDO_DELAY);
  }

  undoRemove() {
    const removed = this.removed();
    if (!removed) return;
    this.store.restore(removed);
    this.removed.set(null);
    if (this.undoTimer) clearTimeout(this.undoTimer);
  }

  dismissUndo() {
    this.removed.set(null);
    if (this.undoTimer) clearTimeout(this.undoTimer);
  }

  clearRoster() {
    if (isPlatformBrowser(this.platformId)) {
      const ok = confirm(this.translate.instant('CLEAR_ALL_CONFIRM'));
      if (!ok) return;
    }
    this.store.clear();
    this.search.set('');
    this.pushUrl(null);
  }

  onPlayersImported(players: ImportedPlayer[]) {
    const count = this.store.addMany(
      players.map((p) => ({
        name: p.name,
        rating: p.rating ?? (this.ratingMode() === 'STARS' ? starsToRating(3) : 50)
      }))
    );
    if (count === 0) return;

    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('TOAST_SUCCESS_SUMMARY'),
      detail: this.translate.instant('PLAYERS_IMPORTED_COUNT', { count })
    });
  }

  // ------------------------------------------------------------------ équipes
  generate() {
    // canGenerate couvre aussi le surplus non désigné: tant que l'utilisateur
    // n'a pas choisi ses remplaçants, on ne tranche pas à sa place.
    if (!this.store.canGenerate()) return;

    const result = this.balancer.generateBalancedTeams(
      this.store.playingPlayers(),
      this.store.benchedPlayers()
    );
    this.store.setComposition(result);
    this.pushUrl(result);
    this.announce('A11Y_TEAMS_READY', { diff: result.metrics.diff });
  }

  // --------------------------------------------------------------- partage
  async copyRecap() {
    const text = this.buildRecap();
    if (!text) return;
    await this.copy(text, 'TOAST_TEXT_COPIED');
  }

  async copyLink() {
    if (!isPlatformBrowser(this.platformId)) return;
    await this.copy(window.location.href, 'TOAST_LINK_COPIED');
  }

  async shareOnWhatsApp() {
    if (!isPlatformBrowser(this.platformId)) return;
    const recap = this.buildRecap();
    if (!recap) return;

    // La fenêtre est ouverte tout de suite: un window.open après un await est
    // bloqué par les navigateurs (plus considéré comme un geste utilisateur).
    const win = window.open('', '_blank');
    const link = await this.shortLink(window.location.href);
    const message = `${recap}\n\n🔗 ${link}`;

    const waUrl = new URL('https://api.whatsapp.com/send');
    waUrl.searchParams.set('text', message);
    if (win) win.location.href = waUrl.toString();
  }

  private buildRecap(): string | null {
    const comp = this.store.composition();
    if (!comp) return null;

    const stars = this.ratingMode() === 'STARS';
    const line = (p: Player) => `• ${p.name} (${stars ? ratingToStars(p.rating) : p.rating})`;

    // Normalisé à l'adoption d'un lien: toujours défini ici.
    const bench = comp.bench;

    return [
      this.translate.instant('WHATSAPP_HEADER'),
      '',
      this.translate.instant('WHATSAPP_HEADER_DESCRIPTION'),
      '',
      this.translate.instant('WHATSAPP_TEAM_A'),
      ...comp.teamA.map(line),
      '',
      this.translate.instant('WHATSAPP_TEAM_B'),
      ...comp.teamB.map(line),
      ...(bench.length > 0
        ? ['', this.translate.instant('WHATSAPP_BENCH'), ...bench.map(line)]
        : []),
      '',
      `${this.translate.instant('GAP_LABEL')}: ${this.toScale(comp.metrics.diff)}`
    ].join('\n');
  }

  private async shortLink(url: string): Promise<string> {
    try {
      const response = await fetch(
        `https://tinyurl.com/api-create.php?url=${encodeURIComponent(url)}`
      );
      if (response.ok) return (await response.text()).trim();
    } catch {
      // Raccourcisseur indisponible: le lien complet fait tout aussi bien l'affaire.
    }
    return url;
  }

  private async copy(text: string, successKey: string) {
    try {
      await navigator.clipboard.writeText(text);
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('TOAST_SUCCESS_SUMMARY'),
        detail: this.translate.instant(successKey)
      });
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('ERROR'),
        detail: this.translate.instant('TOAST_COPY_FAILED')
      });
    }
  }

  // ------------------------------------------------------------------- URL
  private pushUrl(composition: TeamComposition | null) {
    let teams: string | null = null;
    if (composition && isPlatformBrowser(this.platformId)) {
      teams = btoa(encodeURIComponent(JSON.stringify(composition)));
    }
    this.router.navigate([], { queryParams: { teams }, queryParamsHandling: 'merge' });
  }

  private restoreShared(encoded: string) {
    try {
      const composition = JSON.parse(decodeURIComponent(atob(encoded))) as TeamComposition;
      if (!composition?.teamA || !composition?.teamB) return;
      this.store.adoptShared(composition);
    } catch {
      // Lien corrompu: on ignore et on garde l'effectif local.
    }
  }

  private announce(key: string, params?: Record<string, unknown>) {
    this.liveMessage.set(this.translate.instant(key, params));
  }

  // --------------------------------------------------------------- affichage
  initials(name: string): string {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  }

  /** Teinte stable dérivée du nom: repère visuel gratuit dans une longue liste. */
  hue(name: string): number {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) % 360;
    return hash;
  }
}
